import {
  Ban,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  CircleDot,
  CircleHelp,
  CircleMinus,
  CirclePlus,
  CircleX,
  Clock,
  Equal,
  Info,
  LoaderCircle,
  Pencil,
  RefreshCw,
  SkipForward,
  Tags,
  Trash2,
  TriangleAlert,
  Bug,
  type LucideIcon,
} from 'lucide-react';
import type {
  ChangeAction,
  DeploymentStatus,
  DriftState,
  EventLevel,
  ResourceRunStatus,
  RunStatus,
  SecretDriftKind,
  Severity,
} from '../api/types';
import { strings } from '../strings';

export type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'orange' | 'danger';

/** Status presentation: always icon plus text plus color (spec 6.7). */
export interface Presentation {
  label: string;
  tone: Tone;
  icon: LucideIcon;
  spin?: boolean;
}

export const runStatusPresentation: Record<RunStatus, Presentation> = {
  pending: { label: strings.status.run.pending, tone: 'neutral', icon: Clock },
  running: { label: strings.status.run.running, tone: 'info', icon: LoaderCircle, spin: true },
  completed: { label: strings.status.run.completed, tone: 'success', icon: CircleCheck },
  failed: { label: strings.status.run.failed, tone: 'danger', icon: CircleX },
  canceled: { label: strings.status.run.canceled, tone: 'warning', icon: Ban },
};

export const deploymentStatusPresentation: Record<DeploymentStatus, Presentation> = {
  new: { label: strings.status.deployment.new, tone: 'neutral', icon: CircleDashed },
  active: { label: strings.status.deployment.active, tone: 'success', icon: CircleCheck },
  failed: { label: strings.status.deployment.failed, tone: 'danger', icon: CircleX },
  destroyed: { label: strings.status.deployment.destroyed, tone: 'neutral', icon: Trash2 },
};

export const driftPresentation: Record<DriftState, Presentation> = {
  unknown: { label: strings.status.drift.unknown, tone: 'neutral', icon: CircleHelp },
  none: { label: strings.status.drift.none, tone: 'success', icon: CircleCheck },
  detected: { label: strings.status.drift.detected, tone: 'warning', icon: TriangleAlert },
};

export const resourceStatusPresentation: Record<ResourceRunStatus, Presentation> = {
  pending: { label: strings.status.resource.pending, tone: 'neutral', icon: Clock },
  'in-progress': {
    label: strings.status.resource['in-progress'],
    tone: 'info',
    icon: LoaderCircle,
    spin: true,
  },
  completed: { label: strings.status.resource.completed, tone: 'success', icon: CircleCheck },
  failed: { label: strings.status.resource.failed, tone: 'danger', icon: CircleX },
  skipped: { label: strings.status.resource.skipped, tone: 'neutral', icon: SkipForward },
};

/** Plan actions: create green, update amber, replace orange, delete red, no-change grey (spec 6.5). */
export const actionPresentation: Record<ChangeAction, Presentation> = {
  create: { label: strings.status.action.create, tone: 'success', icon: CirclePlus },
  update: { label: strings.status.action.update, tone: 'warning', icon: CircleDot },
  replace: { label: strings.status.action.replace, tone: 'orange', icon: RefreshCw },
  delete: { label: strings.status.action.delete, tone: 'danger', icon: CircleMinus },
  'no-change': { label: strings.status.action['no-change'], tone: 'neutral', icon: Equal },
};

export const levelPresentation: Record<EventLevel, Presentation> = {
  debug: { label: strings.status.level.debug, tone: 'neutral', icon: Bug },
  info: { label: strings.status.level.info, tone: 'info', icon: Info },
  warning: { label: strings.status.level.warning, tone: 'warning', icon: TriangleAlert },
  error: { label: strings.status.level.error, tone: 'danger', icon: CircleAlert },
};

export const secretDriftPresentation: Record<SecretDriftKind, Presentation> = {
  'changed-outside': { label: strings.secrets.driftKinds['changed-outside'], tone: 'warning', icon: Pencil },
  'deleted-outside': { label: strings.secrets.driftKinds['deleted-outside'], tone: 'danger', icon: Trash2 },
  disabled: { label: strings.secrets.driftKinds.disabled, tone: 'danger', icon: Ban },
  expired: { label: strings.secrets.driftKinds.expired, tone: 'danger', icon: Clock },
  'tags-edited': { label: strings.secrets.driftKinds['tags-edited'], tone: 'warning', icon: Tags },
  unmanaged: { label: strings.secrets.driftKinds.unmanaged, tone: 'info', icon: CircleHelp },
  'missing-value': { label: strings.secrets.driftKinds['missing-value'], tone: 'danger', icon: CircleAlert },
  rotated: { label: strings.secrets.driftKinds.rotated, tone: 'info', icon: RefreshCw },
  'rotation-conflict': {
    label: strings.secrets.driftKinds['rotation-conflict'],
    tone: 'warning',
    icon: TriangleAlert,
  },
};

/** Reference status as the platform reports it: `Resolved` is fine, anything else is a problem. */
export function referenceStatusPresentation(status: string | null): Presentation {
  if (status === null) return { label: strings.common.dash, tone: 'neutral', icon: CircleDashed };
  if (status === 'Resolved') return { label: status, tone: 'success', icon: CircleCheck };
  return { label: status, tone: 'danger', icon: CircleX };
}

export const vaultPresentation = {
  notCreated: { label: strings.vault.notCreated, tone: 'warning', icon: CircleDashed },
  ready: { label: strings.vault.ready, tone: 'success', icon: CircleCheck },
} satisfies Record<string, Presentation>;

export const severityPresentation: Record<Severity, Presentation> = {
  error: { label: strings.status.severity.error, tone: 'danger', icon: CircleX },
  warning: { label: strings.status.severity.warning, tone: 'warning', icon: TriangleAlert },
  info: { label: strings.status.severity.info, tone: 'info', icon: Info },
};
